
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { useState } from 'react';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const PricingSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);

  const pricingPlans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Perfect for exploring the oracle',
      features: [
        '10 oracle queries per month',
        'Random tone & voice selection',
        'Access to community questions',
        'Basic spiritual guidance'
      ],
      priceId: null,
      popular: false,
      cta: 'Start Free'
    },
    {
      name: 'Basic',
      price: '$9.95',
      period: 'per month',
      description: 'For regular spiritual seekers',
      features: [
        '100 oracle queries per month',
        'Choose your preferred tone & voice',
        'Priority response time',
        'Access to community questions',
        'Advanced spiritual insights'
      ],
      priceId: 'price_1QpqxqGxqJqxqxqxqxqxqxqx',
      popular: true,
      cta: 'Subscribe Now'
    },
    {
      name: 'Premium',
      price: '$19.95',
      period: 'per month',
      description: 'For devoted wisdom seekers',
      features: [
        '500 oracle queries per month',
        'Choose your preferred tone & voice',
        'Instant response time',
        'Access to community questions',
        'Deep spiritual wisdom',
        'Priority support'
      ],
      priceId: 'price_1QpqxqGxqJqxqxqxqxqxqxqy',
      popular: false,
      cta: 'Subscribe Now'
    }
  ];

  const handleSubscribe = async (plan: typeof pricingPlans[0]) => {
    // If it's the free plan, just navigate to auth
    if (!plan.priceId) {
      navigate('/auth');
      return;
    }

    // For paid plans, check if user is logged in
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      // Not logged in, redirect to auth page
      navigate('/auth');
      return;
    }

    // User is logged in, create checkout session
    setLoading(plan.name);

    try {
      console.log('Starting checkout process...', {
        priceId: plan.priceId,
        userId: session.user.id,
        tier: plan.name
      });

      const functionUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-checkout-session`;
      console.log('Calling Edge Function:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          priceId: plan.priceId,
          userId: session.user.id,
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('Response data:', data);

      if (data.url) {
        console.log('Redirecting to Stripe checkout:', data.url);
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        console.error('No checkout URL in response:', data);
        const errorMessage = data.error || 'Failed to create checkout session. Please try again.';
        alert(errorMessage);
      }
    } catch (error: any) {
      console.error('Checkout error caught:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Error: ${error.message || 'Failed to create checkout session. Please try again.'}`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <section id="pricing" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/f1d9957e92570a0166316477cdee91fb.jpg" 
          alt="Mystical Background" 
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 via-purple-800/70 to-pink-900/80"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-5xl md:text-6xl font-bold text-white mb-6">
            Choose Your Journey
          </h2>
          <p className="text-purple-100 text-lg max-w-2xl mx-auto">
            Select the plan that fits your spiritual exploration needs. Upgrade or cancel anytime.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-white/10 backdrop-blur-md border rounded-3xl p-8 ${
                plan.popular ? 'border-cyan-400 shadow-2xl shadow-cyan-400/20' : 'border-white/20'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-6 py-2 bg-cyan-400 text-purple-900 text-sm font-bold rounded-full whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="font-serif text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="mb-2">
                  <span className="text-5xl font-bold text-white">{plan.price}</span>
                  <span className="text-purple-200 text-sm ml-2">/{plan.period}</span>
                </div>
                <p className="text-purple-200 text-sm">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-white text-sm">
                    <i className="ri-check-line text-lg text-cyan-400 mt-0.5"></i>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan)}
                disabled={loading === plan.name}
                className={`block w-full py-4 text-center font-bold rounded-xl transition-all whitespace-nowrap cursor-pointer ${
                  plan.popular
                    ? 'bg-cyan-400 text-purple-900 hover:shadow-xl hover:shadow-cyan-400/30'
                    : 'bg-white/20 text-white hover:bg-white/30'
                } ${loading === plan.name ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading === plan.name ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Processing...
                  </span>
                ) : (
                  plan.cta
                )}
              </button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-purple-200 text-sm">
            All plans include secure data storage • Cancel anytime • No hidden fees
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
